import { useState } from 'react';
import { useBudgets, useCategories, useCreateBudget, useUpdateBudget, useDeleteBudget } from '@/hooks/useApi';
import { formatRupiah } from '@/lib/format';

export default function BudgetModal({ onClose }) {
  const { data: budgets = [], isLoading } = useBudgets();
  const { data: categories = [] } = useCategories();
  const createBudget = useCreateBudget();
  const updateBudget = useUpdateBudget();
  const deleteBudget = useDeleteBudget();

  const [form, setForm] = useState({ category_id: '', amount: '', period: 'month' });
  const [editId, setEditId] = useState(null);
  const [err, setErr] = useState('');

  const expenseCategories = categories.filter(c => c.type === 'expense');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.amount || isNaN(form.amount) || Number(form.amount) <= 0) {
      return setErr('Masukkan nominal budget yang valid.');
    }
    try {
      const payload = {
        amount: Number(form.amount),
        period: form.period,
        category_id: form.category_id || null,
      };
      if (editId) {
        await updateBudget.mutateAsync({ id: editId, ...payload });
      } else {
        await createBudget.mutateAsync(payload);
      }
      setForm({ category_id: '', amount: '', period: 'month' });
      setEditId(null);
    } catch (e) {
      setErr(e.response?.data?.error || 'Gagal menyimpan budget.');
    }
  };

  const handleDelete = async (id) => {
    setErr('');
    try {
      await deleteBudget.mutateAsync(id);
    } catch (e) {
      setErr(e.response?.data?.error || 'Gagal menghapus budget.');
    }
  };

  const startEdit = (b) => {
    setEditId(b.id);
    setForm({ category_id: b.category_id || '', amount: String(b.amount), period: b.period });
    setErr('');
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px',
      boxSizing: 'border-box'
    }}>
      <div style={{
        background: 'white', border: '2px solid var(--line)', boxShadow: '4px 4px 0 var(--line)',
        width: '100%', maxWidth: '500px', padding: 'clamp(16px, 4vw, 24px)', maxHeight: '90vh',
        overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
          <div>
            <span className="panel-kicker">MANAGE</span>
            <h3 style={{ margin: '4px 0 0', font: "700 20px 'Space Grotesk'", letterSpacing: '-0.5px' }}>Kelola Budget</h3>
          </div>
          <button onClick={onClose} aria-label="Tutup" style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer', lineHeight: 1, padding: '4px' }}>×</button>
        </div>

        {err && (
          <div style={{ background: '#fff0ed', border: '1px solid var(--red)', padding: '10px 14px', marginBottom: '16px', color: 'var(--red)', fontSize: '12px', fontWeight: 700 }}>
            {err}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <select
            value={form.category_id}
            onChange={(e) => setForm(p => ({ ...p, category_id: e.target.value }))}
            style={{ flex: '1 1 150px', border: '2px solid var(--line)', padding: '8px 10px', font: '13px inherit', background: 'white', outline: 'none' }}
          >
            <option value="">Total (semua kategori)</option>
            {expenseCategories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <input
            value={form.amount}
            type="number"
            min="0"
            onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))}
            placeholder="Nominal (Rp)"
            style={{ flex: '1 1 120px', border: '2px solid var(--line)', padding: '8px 12px', font: '13px inherit', outline: 'none' }}
          />
          <select
            value={form.period}
            onChange={(e) => setForm(p => ({ ...p, period: e.target.value }))}
            style={{ border: '2px solid var(--line)', padding: '8px 10px', font: '13px inherit', background: 'white', outline: 'none' }}
          >
            <option value="month">Bulan</option>
            <option value="week">Minggu</option>
          </select>
          <button type="submit" className="primary-button" style={{ whiteSpace: 'nowrap' }}>
            {editId ? '✏️ UPDATE' : '＋ TAMBAH'}
          </button>
          {editId && (
            <button type="button" onClick={() => { setEditId(null); setForm({ category_id: '', amount: '', period: 'month' }); }}
              style={{ border: '2px solid var(--line)', padding: '8px 12px', background: 'transparent', cursor: 'pointer', font: "8px 'Press Start 2P'" }}>
              BATAL
            </button>
          )}
        </form>

        {isLoading ? (
          <p style={{ color: 'var(--muted)', fontSize: '13px' }}>Memuat budget...</p>
        ) : budgets.length === 0 ? (
          <p style={{ color: 'var(--muted)', fontSize: '13px' }}>Belum ada budget. Tambahkan sekarang!</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {budgets.map(b => (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: '1px solid #e8e5e0' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700 }}>
                    {b.category ? b.category.name : 'Total Budget'}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                    {formatRupiah(b.amount)} / {b.period === 'month' ? 'bulan' : 'minggu'}
                  </div>
                </div>
                <button onClick={() => startEdit(b)}
                  style={{ border: '1px solid var(--line)', padding: '5px 9px', background: 'transparent', cursor: 'pointer', fontSize: '12px' }}>
                  ✏️
                </button>
                <button onClick={() => handleDelete(b.id)}
                  style={{ border: '1px solid #ffc5ba', padding: '5px 9px', background: '#fff0ed', color: 'var(--red)', cursor: 'pointer', fontSize: '12px' }}>
                  🗑️
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
